import Phaser from 'phaser';
import { ASSETS } from '../assets/manifest.js';
import { WorldScale } from '../core/WorldScale.js';
import { PALETTE } from '../ui/style.js';
import { SceneKey } from './keys.js';

export interface PreloadOptions {
  /** folder (relative to the page or absolute URL) that holds the assets */
  assetPath: string;
  /** scenes to start once everything is loaded (first one is started, the others launched on top) */
  next: readonly string[];
  /** world → pixel scale the vector assets are rasterised for, default: the whole canvas */
  rasterScale?: WorldScale;
  /** called after the next scenes were started */
  onComplete?: () => void;
}

/** Loads the shared asset manifest and shows a minimal progress bar. */
export class PreloadScene extends Phaser.Scene {
  constructor(private readonly options: PreloadOptions) {
    super(SceneKey.Preload);
  }

  preload(): void {
    const { width, height } = this.scale;
    const ws = new WorldScale(width, height);

    const barW = Math.min(width * 0.6, ws.px(600));
    const barH = ws.px(24);
    const frame = this.add
      .rectangle(width / 2, height / 2, barW, barH, PALETTE.ink)
      .setStrokeStyle(Math.max(1, ws.px(4)), PALETTE.blueLight, 1);
    const bar = this.add.rectangle(frame.x - barW / 2, height / 2, 0, barH, PALETTE.orange).setOrigin(0, 0.5);
    this.load.on(Phaser.Loader.Events.PROGRESS, (p: number) => bar.setSize(barW * p, barH));

    this.load.setPath(this.options.assetPath);
    const raster = this.options.rasterScale ?? ws;
    for (const a of ASSETS) {
      switch (a.type) {
        case 'svg': {
          // spritesheets: whole pixels per frame, so the frames can be cut exactly
          const n = a.frames ?? 1;
          const width = n * Math.ceil(raster.px(a.width / n));
          this.load.svg(a.key, a.file, { width, height: Math.ceil(raster.px(a.height)) });
          break;
        }
        case 'image':
          this.load.image(a.key, a.file);
          break;
      }
    }
  }

  create(): void {
    for (const a of ASSETS) if (a.frames) this.cutFrames(a.key, a.frames);
    const [first, ...overlays] = this.options.next;
    if (first) this.scene.start(first);
    for (const key of overlays) this.scene.launch(key);
    this.options.onComplete?.();
  }

  /** Split a horizontal strip into `count` frames named 0 … count-1. */
  private cutFrames(key: string, count: number): void {
    const texture = this.textures.get(key);
    const source = texture.source[0];
    const w = Math.floor(source.width / count);
    for (let i = 0; i < count; i++) texture.add(i, 0, i * w, 0, w, source.height);
  }
}
